/**
 * The runtime's end of the channel to one page's frame (docs/ideas/app-isolation.md). Each render of a page makes a
 * new one, with its own generation and a 128-bit nonce built into the page. The first hello that comes from that
 * frame's window, with that generation and nonce, gets one port; every later hello is ignored. What the frame sends is
 * untrusted: messages are size-capped JSON, calls are rate-limited, and a closed channel answers nothing.
 */
import { noop } from "../shared/quietly";
import { type CallMessage, type FramePush, MESSAGE_LIMIT, type RuntimeMessage } from "./frame/protocol";

/** What a channel does with a frame's calls: answers them, with a value or a promise, or throws to refuse them. */
export type CallHandler = (method: string, args: unknown) => unknown;
/** Calls a frame may make in a second, which a page drawing at 60 frames a second with a few writes each stays under. */
const CALLS_PER_SECOND = 600;
let generations = 0;

export class FrameChannel {
  readonly generation = ++generations;
  readonly nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  private port: MessagePort | null = null;
  private closed = false;
  private window = { start: 0, count: 0 };
  /** Runs once the frame has its port, to bring it up to date with what changed while it loaded. */
  onOpen: () => void = noop;
  /** Runs before each answer, so a push the answer depends on goes first. */
  beforeReply: () => void = noop;

  constructor(
    private readonly frame: { contentWindow: Window | null },
    private readonly handle: CallHandler,
    private readonly origins: readonly string[],
  ) {}

  get open() {
    return !!this.port && !this.closed;
  }

  /** A message to the runtime's window: true when it was this channel's hello, which it answers with the port. */
  accept(event: Pick<MessageEvent, "source" | "origin" | "data">): boolean {
    if (this.closed || this.port || !this.frame.contentWindow || event.source !== this.frame.contentWindow)
      return false;
    const data = event.data as { coast?: unknown; g?: unknown; n?: unknown } | null;
    if (
      data?.coast !== "hello" ||
      data.g !== this.generation ||
      data.n !== this.nonce ||
      !this.origins.includes(event.origin)
    )
      return false;
    const channel = new MessageChannel();
    this.port = channel.port1;
    this.port.onmessage = (message) => {
      void this.receive(message.data);
    };
    // A page in an opaque origin can't be named, so the port goes to whatever document the frame holds; the
    // generation and nonce, the one-shot hello, and the frame never navigating away keep that document the page's.
    this.frame.contentWindow.postMessage({ coast: "port", g: this.generation }, "*", [channel.port2]);
    this.onOpen();
    return true;
  }

  push(kind: FramePush, value?: unknown) {
    this.send({ t: "push", k: kind, ...(value === undefined ? {} : { v: value }) });
  }

  close() {
    this.closed = true;
    this.port?.close();
    this.port = null;
  }

  private send(message: RuntimeMessage) {
    if (this.open) this.port!.postMessage(JSON.stringify(message));
  }

  private async receive(text: unknown) {
    if (this.closed || typeof text !== "string" || text.length > MESSAGE_LIMIT) return;
    const now = Date.now();
    if (now - this.window.start >= 1000) this.window = { start: now, count: 0 };
    if (++this.window.count > CALLS_PER_SECOND) return;
    let message: CallMessage;
    try {
      message = JSON.parse(text);
    } catch {
      return;
    }
    if (
      message?.t !== "call" ||
      typeof message.m !== "string" ||
      message.m.length > 40 ||
      !Number.isInteger(message.id) ||
      message.id < 0
    )
      return;
    let reply: RuntimeMessage;
    try {
      reply = { t: "reply", id: message.id, ok: true, v: await this.handle(message.m, message.a) };
    } catch (error) {
      const code = (error as { code?: unknown })?.code;
      reply = {
        t: "reply",
        id: message.id,
        ok: false,
        e: {
          message: error instanceof Error ? error.message.slice(0, 300) : "Refused.",
          ...(typeof code === "string" ? { code } : {}),
        },
      };
    }
    if (!message.id || this.closed) return;
    this.beforeReply();
    this.send(reply);
  }
}
